/*
 * Gobal state management for Vue code.
 *
 * Please read before making edits in this file:
 * https://vuex.vuejs.org/en/core-concepts.html
 *
 */

import Vue from "vue";
import Vuex from "vuex";
import eventHub from "./event-hub";

import NProgress from "nprogress";

import {
  resizeAllGridItems,
  checkGridAfterImagesLoaded,
} from "./utilities/grid-utilities";
import sortByProp from "./utilities/sort-by-prop";
import sanitizeCountryName from "./utilities/sanitize-country-name";

Vue.use(Vuex);

// API endpoints
const apiBase = "/wp-json/";
const wpApi = `${apiBase}wp/v2/`;
const mgApi = `${apiBase}mg/v1/`;

export default new Vuex.Store({
  state: {
    marketDataView: "",
    marketDataIsLoading: false,
    marketDataMode: "USDA-data",
    showDollars: true,
    activeUnit: "",
    showQty: true,
    isDesktop: null,
    marketDataYears: 5,
    markets: [],
    activePartners: [],
    activeCommodities: [],
    marketDataSettings: {
      current_market_year: "",
      previous_market_year_label: "",
      current_market_year_label: "",
    },
    colors: [
      "#004b98",
      "#93d7e5",
      "#b4bc1a",
      "#3c3328",
      "#c06b13",
      "#f5dd8d",
      "#c06b13",
      "#0175b9",
      "#888e0a",
      "#cdde31",
      "#7b6756",
    ],
    commodities: [
      {
        icon: "icon-barn",
        color: "#004b98",
        value: "all",
        text: "All",
      },
      {
        icon: "icon-corn",
        color: "#93d7e5",
        value: "corn",
        text: "Corn",
      },
      {
        icon: "icon-barley",
        color: "#b4bc1a",
        value: "barley-barley-products",
        text: "Barley &amp; Barley Products",
      },
      {
        icon: "icon-beef",
        color: "#3c3328",
        value: "beef-beef-products",
        text: "Beef &amp; Beef Products",
      },
      {
        icon: "icon-feed_meal",
        color: "#c06b13",
        value: "corn-gluten-feed&ml",
        text: "Corn Gluten Feed/Meal",
      },
      {
        icon: "icon-grain",
        color: "#f5dd8d",
        value: "crs-grn-products",
        text: "Coarse Grain Products",
      },
      {
        icon: "icon-ddgs",
        color: "#c06b13",
        value: "distillers-grains",
        text: "DDGS",
      },
      {
        icon: "icon-ethanol",
        color: "#0175b9",
        value: "ethanol-(non-bev.)",
        text: "Ethanol",
      },
      {
        icon: "icon-sorghum",
        color: "#888e0a",
        value: "grain-sorghum",
        text: "Sorghum",
      },
      {
        icon: "icon-pork",
        color: "#cdde31",
        value: "pork-pork-products",
        text: "Pork &amp; Pork Products",
      },
      {
        icon: "icon-poultry",
        color: "#7b6756",
        value: "poultry-meat-prods.-(ex.-eggs)",
        text: "Poultry Meat &amp; Product",
      },
    ],
    top_exports_selected: "all",
    export_totals: null,
    market_data: [],
    selectedMarket: "World Total",
    activeMarket: null,
    current_page: 1,
    per_page: 10,
    tax_commodities: [],
    state: [],
    regions: [],
    topics: [],
    tax_countries: [],
    categories: [],
    member_type: [],
    member_area_of_business: [],
    urlHashFilter: {},
    selectedLetter: "",
    newsYear: null,
    newsTaxFilter: {
      category: [],
      topic: [],
      country: [],
      region: [],
      commodity: [],
    },
    membersPaginated: true,
    memberFilter: {
      member_type: null,
      commodity: null,
      search: null,
      member_area_of_business: null,
    },
    newsItems: [],
    members: [],
    totalItemsFound: null,
  },
  mutations: {
    updateDollarsOption(state, value) {
      state.showDollars = value;
    },
    setMarketDataView(state, value) {
      state.marketDataView = value;
    },
    setMarketDataIsLoading(state, value) {
      state.marketDataIsLoading = value;
    },
    updateQtyOption(state, value) {
      state.showQty = value;
    },
    updateIsDesktop(state, value) {
      state.isDesktop = value;
    },
    setActiveUnit(state, value) {
      state.activeUnit = value;
    },
    setMarketDataMode(state, value) {
      state.marketDataMode = value;
    },
    addActiveCommodity(state, key) {
      state.activeCommodities.push(key);
    },
    removeActiveCommodity(state, activeIndex) {
      state.activeCommodities.splice(activeIndex, 1);
    },
    addActivePartner(state, key) {
      state.activePartners.push(key);
    },
    removeActivePartner(state, activeIndex) {
      state.activePartners.splice(activeIndex, 1);
    },
    deactivateAllCommodities(state) {
      state.activeCommodities = [];
    },
    deactivateAllPartners(state) {
      state.activePartners = [];
    },
    setExportTotals(state, obj) {
      state.export_totals = obj;
    },
    setMarkets(state, items) {
      let the_markets = [];
      items.forEach((item) => {
        const obj = {
          value: item.market_name,
          text: item.market_name,
        };
        the_markets.push(obj);
      });

      the_markets = sortByProp(the_markets, "value");
      the_markets.unshift({
        value: "World Total",
        text: "World Total",
      });
      state.markets = the_markets;
    },
    setMarketData(state, items) {
      state.market_data = items;
    },
    setMarketDataSettings(state, payload) {
      state.marketDataSettings = payload;
    },
    setMarket(state, market) {
      state.activeMarket = market;
    },
    setSelectedMarket(state, selection) {
      state.selectedMarket = selection;
    },
    setTopExportsSelected(state, value) {
      state.top_exports_selected = value;
    },
    setCurrentPage(state, page) {
      state.current_page = page;
    },
    incrementCurrentPage(state) {
      state.current_page++;
    },
    setTaxCommodities(state, taxonomyitems) {
      state.tax_commodities = taxonomyitems;
    },
    setRegions(state, taxonomyitems) {
      state.regions = taxonomyitems;
    },
    setState(state, taxonomyitems) {
      state.state = taxonomyitems;
    },
    setTopics(state, taxonomyitems) {
      state.topics = taxonomyitems;
    },
    setTaxCountries(state, taxonomyitems) {
      state.tax_countries = taxonomyitems;
    },
    setCategories(state, taxonomyitems) {
      state.categories = taxonomyitems;
    },
    setMemberTypes(state, taxonomyitems) {
      state.member_type = taxonomyitems;
    },
    setMemberAreaOfBusiness(state, taxonomyitems) {
      state.member_area_of_business = taxonomyitems;
    },
    setNewsItems(state, payload) {
      if (payload.loadMore) {
        // Get existing post IDs to prevent duplicates
        const existingIds = state.newsItems.map((post) => post.ID);

        // Only add posts that don't already exist
        payload.posts.forEach((item) => {
          if (!existingIds.includes(item.ID)) {
            state.newsItems.push(item);
          }
        });
      } else {
        state.newsItems = payload.posts;
      }
    },
    updateUrlHashFilter(state, paylod) {
      state.urlHashFilter = {
        ...state.urlHashFilter,
        ...paylod,
      };
    },
    updateNewsFilter(state, payload) {
      const filterKey =
        payload.taxonomy == "categories" ? "category" : payload.taxonomy;

      if (filterKey == "year") {
        state.newsYear = payload.selected;
      } else {
        // Handle both single values and arrays for multiselect
        if (Array.isArray(payload.selected)) {
          state.newsTaxFilter[filterKey] = payload.selected;
        } else if (typeof payload.selected === "string") {
          state.newsTaxFilter[filterKey] = [];
        } else {
          state.newsTaxFilter[filterKey] = [payload.selected];
        }
      }
    },
    updateMemberFilter(state, payload) {
      if (payload.taxonomy == "alphabet") {
        state.selectedLetter = payload.selected;
      } else {
        state.memberFilter[payload.taxonomy] = payload.selected;
      }
    },
    resetMemberFilter(state) {
      Object.keys(state.memberFilter).forEach((key) => {
        state.memberFilter[key] = null;
      });

      eventHub.$emit("form-reset");
    },
    updateMembersPaginationFlag(state, value) {
      state.membersPaginated = value;
    },
    resetNewsFilter(state) {
      state.newsYear = null;

      Object.keys(state.newsTaxFilter).forEach((key) => {
        state.newsTaxFilter[key] = [];
      });

      eventHub.$emit("form-reset");
    },
    setTotalFound(state, total) {
      state.totalItemsFound = total;
    },
    setMembers(state, payload) {
      state.members = payload;
    },
    updateMemberSearch(state, keyword) {
      state.memberFilter.search = keyword;
    },
  },
  getters: {
    topTenMarkets: (state) => {
      if (state.market_data.length == 0) return [];

      let topTen = [];
      for (let i = 0; i < state.market_data.length; i++) {
        const market = state.market_data[i];
        if (
          market[state.top_exports_selected] &&
          market[state.top_exports_selected]["current_market_year_quantity"] !=
            null
        ) {
          topTen.push(state.market_data[i]);

          if (topTen.length == 10) break;
        }
      }
      return topTen;
    },
    getCommodityProperty: (state) => (payload) => {
      if (!payload || !payload.key || !payload.property) return;
      for (let index = 0; index < state.commodities.length; index++) {
        const commodity = state.commodities[index];
        if (commodity.value == payload.key) {
          return commodity[payload.property];
        }
      }
    },
    isCommodity: (state) => (key) => {
      for (let index = 0; index < state.commodities.length; index++) {
        const commodity = state.commodities[index];
        if (commodity.value == key) {
          return true;
        }
      }
      return false;
    },
    sortedData: (state) => (
      data = {},
      sort_property = "current_market_year_quantity",
    ) => {
      const newData = {};
      let returnData = {};
      const dataToSort = [];
      for (const key in data) {
        if (Object.hasOwnProperty.call(data, key)) {
          const value = data[key];
          const isObject =
            typeof value === "object" &&
            !Array.isArray(value) &&
            value !== null;
          if (isObject) {
            dataToSort.push(value);
          } else {
            newData[key] = value;
          }
        }
      }
      const sortedData = sortByProp(dataToSort, sort_property, true);
      sortedData.reverse().forEach((element) => {
        if (element.slug && "all" !== element.slug) {
          newData[element.slug] = element;
        }
      });

      if (data && data.all) {
        returnData.all = data.all;
      }

      returnData = { ...returnData, ...newData };

      return returnData;
    },
  },
  actions: {
    handleFilters({ commit, state }, payload) {
      if (payload.type == "member-filter") {
        commit("updateMemberFilter", payload);
      } else {
        commit("updateNewsFilter", payload);
      }
    },
    santizeCountryNames({ state }, markets) {
      markets.forEach((item) => {
        item.market_name = sanitizeCountryName(item.market_name);
      });
    },
    handleActiveUnit({ commit, state }) {
      if (
        state.showQty &&
        state.marketDataMode == "USDA-data" &&
        state.activeCommodities.length == 1 &&
        state.activeCommodities[0] == "ethanol-(non-bev.)"
      ) {
        commit("setActiveUnit", "GAL");
      } else {
        if (state.top_exports_selected == "ethanol-(non-bev.)") {
          commit("setActiveUnit", "GAL");
        } else {
          commit("setActiveUnit", "MT");
        }
      }
    },
    activateSinglePartner({ commit, state }, key) {
      commit("deactivateAllPartners");
      commit("addActivePartner", key);
    },
    activateSingleCommodity({ commit, state }, key) {
      commit("deactivateAllCommodities");
      commit("addActiveCommodity", key);
    },
    activateAllCommodities({ commit, state }) {
      state.commodities.forEach((commodity) => {
        if (state.activeCommodities.indexOf(commodity.value) == -1) {
          commit("addActiveCommodity", commodity.value);
        }
      });
    },
    selectMarket({ commit, state }) {
      if (state.selectedMarket == "World Total") {
        commit("setMarket", state.export_totals);
      } else {
        for (let index = 0; index < state.market_data.length; index++) {
          const element = state.market_data[index];
          if (element.market_name == state.selectedMarket) {
            commit("setMarket", element);
            break;
          }
        }
      }
    },
    getExportTotals({ commit, state }, endpoint = "export-totals") {
      return new Promise((resolve, reject) => {
        NProgress.start();
        // timestamp is a workaround to prevent caching of the response
        // we need to address a WP rocket issue:
        // https://intranet.matrixgroup.net/webmaster/index.cfm?action=showmessage&messageid=137016
        $.ajax({
          url: `${mgApi}${endpoint}`,
          data: {
            timestamp: Date.now(),
          },
        })
          .then(
            (response) => {
              commit("setExportTotals", response);

              resolve();
            },
            (error) => {
              console.log("There was a problem retrieving Export Totals");
              reject();
            },
          )
          .always(() => {
            NProgress.done();
          });
      });
    },
    getExportTotalsFeedGrain({ commit, state, dispatch }) {
      return dispatch("getExportTotals", "export-totals-feed-grain");
    },
    getExportTotalsCornEquiv({ commit, state, dispatch }) {
      return dispatch("getExportTotals", "export-totals-corn-equiv");
    },
    getMarketDataSettings({ commit, state }) {
      return new Promise((resolve, reject) => {
        NProgress.start();
        commit("setMarketDataIsLoading", true);
        $.ajax({
          url: `${mgApi}market-data-settings`,
        })
          .then(
            (response) => {
              commit("setMarketDataSettings", response);
              resolve();
            },
            (error) => {
              console.log(
                "There was a problem retrieving Market Data Settings",
              );
              reject();
            },
          )
          .always(() => {
            NProgress.done();
            commit("setMarketDataIsLoading", false);
          });
      });
    },
    getMarketData({ dispatch, commit, state }, endpoint = "market-data") {
      return new Promise((resolve, reject) => {
        NProgress.start();

        commit("setMarketDataIsLoading", true);

        const requestParams = {
          exclude_fta_partners: false,
        };

        if ("top-export-partners" === state.marketDataView) {
          requestParams.exclude_fta_partners = true;
        }

        $.ajax({
          url: `${mgApi}${endpoint}`,
          data: requestParams,
        })
          .then(
            (response) => {
              dispatch("santizeCountryNames", response);

              commit("setMarkets", response);
              commit("setMarketData", response);

              resolve();
            },
            (error) => {
              console.log("There was a problem retrieving Market Data");
              reject();
            },
          )
          .always(() => {
            NProgress.done();
            commit("setMarketDataIsLoading", false);
          });
      });
    },
    getMarketDataFeedGrain({ dispatch, commit, state }) {
      return dispatch("getMarketData", "market-data-feed-grain");
    },
    getMarketDataCornEquiv({ dispatch, commit, state }) {
      return dispatch("getMarketData", "market-data-corn-equiv");
    },
    getTaxonomy({ commit, state }, payload) {
      // Handle both old format (string) and new format (object with taxonomy and feedType)
      const taxonomy = typeof payload === "string" ? payload : payload.taxonomy;
      const feedType = typeof payload === "object" ? payload.feedType : null;

      if (!taxonomy) return;

      return new Promise((resolve, reject) => {
        NProgress.start();

        // Use custom reports endpoint for reports feed to only show terms with available posts
        const endpoint =
          feedType === "reports"
            ? `${mgApi}reports-taxonomies`
            : `${wpApi}${taxonomy}`;
        const requestData =
          feedType === "reports"
            ? { taxonomy: taxonomy }
            : { per_page: 100, hide_empty: true };

        $.ajax({
          url: endpoint,
          data: requestData,
        })
          .then(
            (response) => {
              if (taxonomy == "commodity") {
                commit("setTaxCommodities", response);
              } else if (taxonomy == "state") {
                commit("setState", response);
              } else if (taxonomy == "region") {
                commit("setRegions", response);
              } else if (taxonomy == "topic") {
                commit("setTopics", response);
              } else if (taxonomy == "country") {
                commit("setTaxCountries", response);
              } else if (taxonomy == "categories") {
                commit("setCategories", response);
              } else if (taxonomy == "member_type") {
                commit("setMemberTypes", response);
              } else if (taxonomy == "member_area_of_business") {
                commit("setMemberAreaOfBusiness", response);
              }

              resolve();
            },
            (error) => {
              console.log(
                `There was an error trying to retrieve the ${taxonomy} taxonomy`,
              );

              reject();
            },
          )
          .always(() => {
            NProgress.done();
          });
      });
    },
    getNewsroomFeed({ commit, state }, loadMore) {
      if (loadMore) {
        commit("incrementCurrentPage");
      } else {
        commit("setCurrentPage", 1);
      }

      NProgress.start();
      $.ajax({
        url: `${mgApi}newsroom`,
        data: {
          page: state.current_page,
          taxonomies: state.newsTaxFilter,
          year: state.newsYear,
        },
      })
        .then(
          (response) => {
            let payload = {
              posts: response.posts,
              loadMore: loadMore,
            };
            commit("setNewsItems", payload);
            commit("setTotalFound", response.number_of_posts);

            Vue.nextTick(() => {
              checkGridAfterImagesLoaded();
            });
          },
          (error) => {
            console.log("There was a error getting the newsroom feed");
          },
        )
        .always(() => {
          NProgress.done();
        });
    },
    getReportsFeed({ commit, state }, loadMore) {
      if (loadMore) {
        commit("incrementCurrentPage");
      } else {
        commit("setCurrentPage", 1);
      }

      NProgress.start();
      $.ajax({
        url: `${mgApi}reports`,
        data: {
          page: state.current_page,
          taxonomies: state.newsTaxFilter,
          year: state.newsYear,
        },
      })
        .then(
          (response) => {
            let payload = {
              posts: response.posts,
              loadMore: loadMore,
            };
            commit("setNewsItems", payload);
            commit("setTotalFound", response.number_of_posts);

            Vue.nextTick(() => {
              checkGridAfterImagesLoaded();
            });
          },
          (error) => {
            console.log("There was a error getting the reports feed");
          },
        )
        .always(() => {
          NProgress.done();
        });
    },
    getMembers({ commit, state }, resetPagination = false) {
      if (resetPagination) {
        commit("setCurrentPage", 1);
      }

      let params = {
        page: state.current_page,
        per_page: state.per_page,
      };

      if (!state.membersPaginated) {
        params.all = 1;
      }

      Object.keys(state.memberFilter).map((key, index) => {
        const filterParam = state.memberFilter[key];

        if (filterParam) {
          params[key] = filterParam;
        }
      });

      return new Promise((resolve, reject) => {
        NProgress.start();
        $.ajax({
          url: `${mgApi}member`,
          data: params,
        })
          .then(
            (response) => {
              response.posts.forEach((item) => {
                if (item.member_type_info) {
                  let memberTypes = [];
                  item.member_type_info.forEach((type) => {
                    memberTypes.push(type.name);
                  });
                  item.member_type_display = memberTypes.join(",");
                }

                if (item.member_area_of_business_info) {
                  let areasOfBusiness = [];
                  item.member_area_of_business_info.forEach((type) => {
                    areasOfBusiness.push(type.name);
                  });
                  item.member_area_of_business_display = areasOfBusiness.join(
                    ", ",
                  );
                }
              });

              commit("setMembers", response.posts);
              commit("setTotalFound", response.number_of_posts);

              resolve();
            },
            (error) => {
              console.log("There was an error getting members");

              reject();
            },
          )
          .always(() => {
            NProgress.done();
          });
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/js/store.js